// F2 animation debug panel: every loaded clip as a button so we can review
// and tune animations together in-game. Click = play full body, Shift+click =
// upper body only, Ctrl+click = set as upper hold (Ctrl+click again to clear),
// Alt+click = loop as base locomotion (requires Freeze so the controller
// doesn't overwrite it). "Freeze controller" pauses the player brain while the
// animator keeps running (window.__freezePlayer, respected in main.js).
// The MOVES tab auto-generates the full input-combo → animation mapping by
// probing resolveAttack/pickLoco, so it can never drift out of sync.
import { WEAPONS, WEAPON_ORDER, resolveAttack, pickLoco } from '../player/weapons';

function probeMoves() {
  const sections = [];
  const baseCtx = () => ({ dirs: { w: 0, a: 0, s: 0, d: 0 }, chain: 0, stance: 'stand', airborne: false, blocking: false, sprint: false });
  const COMBOS = [
    ['LMB', {}],
    ['W + LMB', { dirs: { w: 1 } }],
    ['A + LMB', { dirs: { a: 1 } }],
    ['D + LMB', { dirs: { d: 1 } }],
    ['S + LMB', { dirs: { s: 1 } }],
    ['W+A + LMB', { dirs: { w: 1, a: 1 } }],
    ['W+D + LMB', { dirs: { w: 1, d: 1 } }],
    ['S+A + LMB', { dirs: { s: 1, a: 1 } }],
    ['S+D + LMB', { dirs: { s: 1, d: 1 } }],
    ['Sprint + LMB', { sprint: true }],
    ['RMB(block) + LMB', { blocking: true }],
    ['mid-air + LMB', { airborne: true }],
  ];
  for (const w of WEAPON_ORDER) {
    const wpn = WEAPONS[w];
    const rows = [];
    if (wpn.kind === 'gun') {
      rows.push({ combo: 'LMB', label: wpn.auto ? 'Fire (auto)' : 'Fire', clip: wpn.fire });
      rows.push({ combo: 'RMB (hold)', label: 'Aim down sights', clip: wpn.aimIdle || wpn.aim || '—' });
      rows.push({ combo: 'R', label: 'Reload', clip: wpn.reload });
      if (wpn.reloadProne) rows.push({ combo: 'R (prone)', label: 'Prone reload', clip: wpn.reloadProne });
      rows.push({ combo: 'F', label: wpn.meleeLabel || 'Gun melee', clip: wpn.meleeClip });
      if (wpn.equip) rows.push({ combo: `equip (${WEAPON_ORDER.indexOf(w) + 1})`, label: 'Draw weapon', clip: wpn.equip });
    } else {
      for (const [name, over] of COMBOS) {
        for (const chain of [0, 1, 2]) {
          const ctx = baseCtx();
          Object.assign(ctx, over);
          if (over.dirs) ctx.dirs = { ...baseCtx().dirs, ...over.dirs };
          ctx.chain = chain;
          let m;
          try { m = resolveAttack(w, ctx); } catch { m = null; }
          if (!m) continue;
          const prev = rows[rows.length - 1];
          const combo = chain === 0 ? name : `${name} ×${chain + 1}`;
          if (prev && prev.clip === m.clip && prev.label === m.label && prev.baseCombo === name) continue;
          rows.push({ combo, baseCombo: name, label: m.label, clip: m.clip });
        }
      }
    }
    sections.push({ title: `${wpn.label ?? w}  (${w})`, rows });
  }
  // locomotion — the new movement scheme: unaimed all travel is FORWARD
  const loco = [];
  for (const stance of ['stand', 'crouch', 'prone']) {
    for (const [name, x, z, mode] of [
      ['idle', 0, 0, 'run'], ['move', 0, 1, 'run'], ['walk (Alt)', 0, 1, 'walk'], ['sprint', 0, 1, 'sprint'],
    ]) {
      try {
        const r = pickLoco(stance, x, z, mode, { aiming: false, weapon: 'fists' });
        loco.push({ combo: `${stance} · ${name}`, label: 'locomotion', clip: r.name });
      } catch { /* not applicable */ }
    }
  }
  for (const [name, x, z] of [['ADS fwd', 0, 1], ['ADS left', -1, 0], ['ADS right', 1, 0], ['ADS back', 0, -1]]) {
    try {
      const r = pickLoco('stand', x, z, 'walk', { aiming: true, weapon: 'rifle' });
      loco.push({ combo: `rifle · ${name}`, label: 'aim walk', clip: r.name });
    } catch { /* not applicable */ }
  }
  sections.push({ title: 'LOCOMOTION (unaimed = always forward, body turns)', rows: loco });
  sections.push({
    title: 'SYSTEM',
    rows: [
      { combo: 'Space', label: 'Jump / vault / climb', clip: 'jump · vault · climb-top' },
      { combo: 'Space (running)', label: 'Running jump', clip: 'jump_running' },
      { combo: 'V / dodge keys', label: 'Rolls & dodges', clip: 'roll-fwd · roll-sprint · dodge-right · prone-roll' },
      { combo: 'C / X', label: 'Stance transitions', clip: 'stand_to_crouch · crouch_to_prone …' },
      { combo: 'Q / E', label: 'Lean (spine twist)', clip: '(procedural)' },
      { combo: 'Alt (hold)', label: 'Free-look', clip: '(camera only)' },
      { combo: 'Ctrl', label: 'Sneak', clip: '(volume + speed)' },
    ],
  });
  return sections;
}

export function buildAnimDebug(animator, input) {
  const css = document.createElement('style');
  css.textContent = `
    #animdbg { position:fixed; right:12px; top:12px; bottom:12px; width:320px; z-index:40;
      display:none; flex-direction:column; font:12px/1.45 monospace; color:#cfd4c8;
      background:rgba(10,12,9,.92); border:1px solid #3a4033; border-radius:6px; }
    #animdbg header { padding:8px 10px; border-bottom:1px solid #3a4033; }
    #animdbg header b { color:#e8d9a0; }
    #animdbg .row { display:flex; gap:8px; align-items:center; margin-top:6px; }
    #animdbg input[type=text] { flex:1; background:#171a14; color:#cfd4c8; border:1px solid #3a4033;
      border-radius:3px; padding:3px 6px; font:inherit; }
    #animdbg label { user-select:none; cursor:pointer; }
    #animdbg #animdbg-status { padding:6px 10px; border-bottom:1px solid #3a4033; color:#9fb08a;
      white-space:pre-wrap; }
    #animdbg #animdbg-list { flex:1; overflow-y:auto; padding:6px; }
    #animdbg button.clip { display:block; width:100%; text-align:left; margin:0 0 3px 0;
      background:#1c2018; color:#cfd4c8; border:1px solid #333a2d; border-radius:3px;
      padding:3px 8px; font:inherit; cursor:pointer; }
    #animdbg button.clip:hover { background:#2a3324; border-color:#5a6a4a; }
    #animdbg button.clip.active { background:#3a4a2e; color:#fff; border-color:#8aa06a; }
    #animdbg footer { padding:6px 10px; border-top:1px solid #3a4033; color:#8a927e; }
  `;
  document.head.appendChild(css);

  const panel = document.createElement('div');
  panel.id = 'animdbg';
  panel.innerHTML = `
    <header>
      <b>ANIMATIONS</b> <span id="animdbg-count"></span>
      <button id="animdbg-moves" class="clip" style="width:auto;display:inline-block;margin-left:8px">MOVES ⇄</button>
      <div class="row">
        <input id="animdbg-search" type="text" placeholder="search… (esc clears)">
        <label><input id="animdbg-freeze" type="checkbox"> freeze</label>
      </div>
    </header>
    <div id="animdbg-status"></div>
    <div id="animdbg-list"></div>
    <footer>click: full · shift: upper · ctrl: hold · alt: base-loop (freeze)<br>
    F2 closes — click the canvas to resume play</footer>`;
  document.body.appendChild(panel);

  // MOVES overview: full input-combo → animation table, auto-probed
  const moves = document.createElement('div');
  moves.id = 'animdbg-movespanel';
  moves.style.cssText = 'position:fixed;left:12px;top:12px;bottom:12px;width:460px;z-index:40;'
    + 'display:none;flex-direction:column;font:12px/1.5 monospace;color:#cfd4c8;'
    + 'background:rgba(10,12,9,.94);border:1px solid #3a4033;border-radius:6px;';
  const movesInner = document.createElement('div');
  movesInner.style.cssText = 'flex:1;overflow-y:auto;padding:8px 10px;';
  const movesHead = document.createElement('div');
  movesHead.style.cssText = 'padding:8px 10px;border-bottom:1px solid #3a4033;';
  movesHead.innerHTML = '<b style="color:#e8d9a0">MOVES — input combo → animation</b>'
    + '<span style="color:#8a927e"> (click a row to preview the clip)</span>';
  moves.appendChild(movesHead);
  moves.appendChild(movesInner);
  document.body.appendChild(moves);
  let movesBuilt = false;
  const buildMovesTable = () => {
    movesInner.innerHTML = '';
    for (const sec of probeMoves()) {
      const h = document.createElement('div');
      h.style.cssText = 'color:#e8d9a0;margin:10px 0 4px;border-bottom:1px solid #3a4033;';
      h.textContent = sec.title;
      movesInner.appendChild(h);
      for (const r of sec.rows) {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;gap:8px;cursor:pointer;padding:1px 2px;border-radius:3px;';
        row.onmouseenter = () => { row.style.background = '#2a3324'; };
        row.onmouseleave = () => { row.style.background = ''; };
        row.innerHTML = `<span style="width:150px;color:#9fb08a">${r.combo}</span>`
          + `<span style="flex:1">${r.label}</span>`
          + `<span style="color:#7a8a9a">${r.clip}</span>`;
        row.addEventListener('click', () => {
          const clip = String(r.clip).split('·')[0].trim();
          if (animator.has?.(clip) || animator.clips[clip]) animator.playFull(clip, 1);
        });
        movesInner.appendChild(row);
      }
    }
    movesBuilt = true;
  };
  panel.querySelector('#animdbg-moves').addEventListener('click', () => {
    const on = moves.style.display !== 'flex';
    moves.style.display = on ? 'flex' : 'none';
    if (on && !movesBuilt) buildMovesTable();
  });

  const list = panel.querySelector('#animdbg-list');
  const search = panel.querySelector('#animdbg-search');
  const freeze = panel.querySelector('#animdbg-freeze');
  const status = panel.querySelector('#animdbg-status');
  const count = panel.querySelector('#animdbg-count');

  const names = Object.keys(animator.clips).sort();
  count.textContent = `(${names.length})`;
  const buttons = new Map();
  for (const name of names) {
    const b = document.createElement('button');
    b.className = 'clip';
    b.textContent = `${name}  ·  ${animator.dur(name).toFixed(2)}s`;
    b.addEventListener('click', (e) => {
      if (e.ctrlKey) {
        animator.setHold(animator.hold?.name === name ? null : name);
      } else if (e.altKey) {
        if (!freeze.checked) { freeze.checked = true; freeze.dispatchEvent(new Event('change')); }
        animator.setBase(name, 1);
      } else if (e.shiftKey) {
        animator.playUpper(name, 1);
      } else {
        animator.playFull(name, 1);
      }
    });
    buttons.set(name, b);
    list.appendChild(b);
  }

  search.addEventListener('input', () => {
    const q = search.value.trim().toLowerCase();
    for (const [name, b] of buttons) b.style.display = !q || name.includes(q) ? 'block' : 'none';
  });
  search.addEventListener('keydown', (e) => {
    e.stopPropagation(); // panel typing must not move the player
    if (e.key === 'Escape') { search.value = ''; search.dispatchEvent(new Event('input')); }
  });
  freeze.addEventListener('change', () => { window.__freezePlayer = freeze.checked; });

  setInterval(() => {
    if (panel.style.display !== 'flex') return;
    status.textContent =
      `base  ${animator.base?.name ?? '—'} @${(animator.base?.rate ?? 1).toFixed(2)}\n` +
      `hold  ${animator.hold?.name ?? '—'}   upper ${animator.upperShot?.name ?? '—'}\n` +
      `full  ${animator.fullShot?.name ?? '—'}${window.__freezePlayer ? '   [FROZEN]' : ''}`;
    for (const [name, b] of buttons) {
      b.classList.toggle('active',
        name === animator.base?.name || name === animator.hold?.name ||
        name === animator.upperShot?.name || name === animator.fullShot?.name);
    }
  }, 250);

  const api = {
    get open() { return panel.style.display === 'flex'; },
    toggle() {
      const on = panel.style.display !== 'flex';
      panel.style.display = on ? 'flex' : 'none';
      if (on) {
        document.exitPointerLock?.();
        search.focus();
      } else {
        moves.style.display = 'none';
        if (freeze.checked) {
          freeze.checked = false;
          freeze.dispatchEvent(new Event('change'));
        }
      }
    },
  };
  window.__animdbg = api; // test hook
  return api;
}
